import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';
import JWTDecoder from 'jwt-decode';
import { Grid, Button, TextField, MenuItem, Card, CardContent } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import Api from '../../Api';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  details: {
    whiteSpace: 'pre-line',
    fontSize: 13
  }
}));

const EVENT_LABELS = {
  folder_create: 'Carpeta creada',
  folder_rename: 'Carpeta renombrada',
  folder_move: 'Carpeta movida',
  folder_delete: 'Carpeta eliminada',
  file_upload: 'Archivo subido',
  file_rename: 'Archivo renombrado',
  file_move: 'Archivo movido',
  file_delete: 'Archivo eliminado',
  grant_create: 'Acceso otorgado',
  grant_delete: 'Acceso quitado'
};

const TARGET_LABELS = {
  folder: 'Carpeta',
  file: 'Archivo'
};

// Max items listed for a folder_delete subtree before summarizing ("y N más").
const MAX_LISTED = 10;

// El driver mysql2 (DB_TIMEZONE=-03:00) le aplica un offset extra a los
// CURRENT_TIMESTAMP que ya vienen en UTC, dando +3h. Compensamos -3h para
// mostrar la hora real de Argentina (mismo workaround que Drive/Informes).
const formatDate = value => {
  if (!value) {
    return '';
  }
  return moment
    .utc(value)
    .subtract(3, 'hours')
    .local()
    .format('DD/MM/YYYY HH:mm');
};

const parseDetails = details => {
  if (!details) {
    return {};
  }
  if (typeof details === 'string') {
    try {
      return JSON.parse(details) || {};
    } catch (e) {
      return {};
    }
  }
  return details;
};

// format(item) -> line text. count: *_count sent by the API when the list was cut.
const listSection = (title, items, count, format) => {
  const list = items || [];
  const total = typeof count === 'number' ? count : list.length;
  if (total === 0) {
    return [];
  }
  const lines = [title + ' (' + total + '):'];
  list.slice(0, MAX_LISTED).forEach(item => {
    lines.push('• ' + format(item));
  });
  if (total > MAX_LISTED) {
    lines.push('… y ' + (total - MAX_LISTED) + ' más');
  }
  return lines;
};

// Readable one/multi-line summary of the details JSON, per event.
const describeDetails = (event, rawDetails) => {
  const details = parseDetails(rawDetails);
  switch (event) {
    case 'folder_rename':
    case 'file_rename':
      return '«' + (details.old_name || '') + '» → «' + (details.new_name || '') + '»';
    case 'folder_move':
    case 'file_move':
      return 'De: ' + (details.from_path || '-') + '\nA: ' + (details.to_path || '-');
    case 'folder_delete': {
      // folders[].path is the subfolder's own (inclusive) path; files[].path is its folder's path.
      const folderLines = listSection('Subcarpetas eliminadas', details.folders, details.folders_count, f => f.path || f.name);
      const fileLines = listSection('Archivos eliminados', details.files, details.files_count, f =>
        f.path ? f.path + ' / ' + f.name : f.name
      );
      const lines = folderLines.concat(fileLines);
      if (details.truncated) {
        lines.push('(detalle recortado)');
      }
      return lines.length > 0 ? lines.join('\n') : 'Carpeta vacía';
    }
    case 'grant_create':
    case 'grant_delete':
      return (
        'Cliente: ' +
        (details.client_name || '#' + details.client_id) +
        '\nSucursal: ' +
        (details.plant_id == null ? 'Toda la empresa' : details.plant_name || '#' + details.plant_id)
      );
    default:
      return '';
  }
};

const userLabel = user => {
  const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ');
  return fullName ? fullName + (user.email ? ' (' + user.email + ')' : '') : user.email || 'Usuario #' + user.id;
};

const DriveActivity = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const jwt = localStorage.getItem('access-jwt');
  var jwtUser = [];
  if (jwt !== null) {
    jwtUser = JWTDecoder(localStorage.getItem('access-jwt'));
  }
  const isAdmin = jwtUser.u_role_id === 1;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [users, setUsers] = useState([]);

  // Filters being edited vs filters applied to the query.
  const [event, setEvent] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filters, setFilters] = useState({});

  const loadActivity = (pageToLoad, limit, appliedFilters) => {
    var params = ['limit=' + limit, 'offset=' + pageToLoad * limit];
    Object.keys(appliedFilters).forEach(key => {
      if (appliedFilters[key]) {
        params.push(key + '=' + encodeURIComponent(appliedFilters[key]));
      }
    });
    Api.get('/drive_activity_log?' + params.join('&'))
      .then(function(response) {
        const data = response.data.data || {};
        setRows(data.results || []);
        setTotal(data.total || 0);
      })
      .catch(function(error) {
        setRows([]);
        setTotal(0);
        var message = 'Error de conexion, contacta a soporte';
        if (error.response) {
          message = 'No se pudo cargar la actividad, intenta nuevamente.';
        }
        enqueueSnackbar(message, {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'error'
        });
      });
  };

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    Api.get('/users')
      .then(function(response) {
        // Only managers (roles 1/2) act on the Drive.
        setUsers((response.data.data || []).filter(u => Number(u.role_id) !== 3));
      })
      .catch(function() {
        setUsers([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    loadActivity(page, rowsPerPage, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filters]);

  const handleApplyFilters = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      enqueueSnackbar('La fecha "Desde" no puede ser posterior a la fecha "Hasta".', {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        variant: 'warning'
      });
      return;
    }
    setPage(0);
    setFilters({
      event: event,
      user_id: selectedUser ? selectedUser.id : '',
      date_from: dateFrom,
      date_to: dateTo
    });
  };

  const handleClearFilters = () => {
    setEvent('');
    setSelectedUser(null);
    setDateFrom('');
    setDateTo('');
    setPage(0);
    setFilters({});
  };

  if (!isAdmin) {
    return <Redirect to="/" />;
  }

  const data = rows.map(row => ({
    created_on: formatDate(row.created_on),
    user_name: row.user_name || (row.user_id ? 'Usuario #' + row.user_id : '-'),
    event: EVENT_LABELS[row.event] || row.event,
    target: (TARGET_LABELS[row.target_type] ? TARGET_LABELS[row.target_type] + ': ' : '') + (row.target_name || ''),
    target_path: row.target_path || '',
    details: describeDetails(row.event, row.details)
  }));

  const columns = [
    { name: 'created_on', label: 'Fecha', options: { filter: false, sort: false } },
    { name: 'user_name', label: 'Usuario', options: { filter: false, sort: false } },
    { name: 'event', label: 'Evento', options: { filter: false, sort: false } },
    { name: 'target', label: 'Objeto', options: { filter: false, sort: false } },
    { name: 'target_path', label: 'Ruta', options: { filter: false, sort: false } },
    {
      name: 'details',
      label: 'Detalles',
      options: {
        filter: false,
        sort: false,
        customBodyRender: value => <div className={classes.details}>{value}</div>
      }
    }
  ];

  const options = {
    serverSide: true,
    count: total,
    page: page,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [25, 50, 100],
    responsive: 'standard',
    selectableRows: 'none',
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    sort: false,
    onTableChange: (action, tableState) => {
      if (action === 'changePage') {
        setPage(tableState.page);
      } else if (action === 'changeRowsPerPage') {
        setPage(0);
        setRowsPerPage(tableState.rowsPerPage);
      }
    },
    textLabels: {
      body: {
        noMatch: 'No hay actividad registrada',
        toolTip: 'Order',
        columnHeaderTooltip: column => `Ordernar por ${column.label}`
      },
      pagination: {
        next: 'Pagina siguiente',
        previous: 'Pagina anterior',
        rowsPerPage: 'Filas por página:',
        displayRows: 'de'
      },
      toolbar: {
        search: 'Buscar',
        downloadCsv: 'Descargar CSV',
        print: 'Imprimir',
        filterTable: 'Filtros'
      },
      filter: {
        all: 'Todos',
        title: 'FILTROS',
        reset: 'BORRAR FILTROS'
      },
      selectedRows: {
        text: 'fila(s) seleccionada',
        delete: 'Eliminar',
        deleteAria: 'Eliminar filas seleccionadas'
      }
    }
  };

  return (
    <div className={classes.root}>
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth variant="outlined" label="Evento" value={event} onChange={e => setEvent(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Object.keys(EVENT_LABELS).map(key => (
                  <MenuItem key={key} value={key}>
                    {EVENT_LABELS[key]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={userLabel}
                value={selectedUser}
                onChange={(e, value) => setSelectedUser(value)}
                renderInput={params => <TextField {...params} variant="outlined" label="Usuario" />}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                label="Desde"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                label="Hasta"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button color="primary" variant="contained" onClick={handleApplyFilters} fullWidth>
                Filtrar
              </Button>
              <Button onClick={handleClearFilters} fullWidth style={{ marginTop: 4 }}>
                Limpiar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <div className={classes.content}>
        <MUIDataTable title={'Actividad Documentos'} data={data} columns={columns} options={options} />
      </div>
    </div>
  );
};

export default DriveActivity;
